import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, makeStyles, Theme } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import { useTranslation } from 'react-i18next';
import {
  includeOrgFile,
  excludeOrgFile,
  listContextFiles,
  type ProjectsApiOptions,
} from '../../api/projects';
import { canManageArtifacts } from './ProjectDetailsPage';
import { OrgContextSetSelector } from './OrgContextSetSelector';
import { type OrgContextFile, type Project } from './types';

const MOCK_ORG_FILES: OrgContextFile[] = [
  {
    id: 'org-1',
    name: 'company-coding-standard.pdf',
    fileTypeTag: 'GENERAL',
    status: 'READY',
    isIncluded: true,
  },
  {
    id: 'org-2',
    name: 'sys2-template.reqif',
    fileTypeTag: 'SYS2_REQUIREMENT',
    status: 'READY',
    isIncluded: false,
  },
  {
    id: 'org-3',
    name: 'network-descriptor-v2.xml',
    fileTypeTag: 'API_DESCRIPTOR',
    status: 'READY',
    isIncluded: true,
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  readOnlyNotice: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
}));

type Props = {
  project: Project;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
};

export function OrgContextSetPanel({
  project,
  apiOptions,
  isPreviewMode,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const readOnly = !canManageArtifacts(project);

  const [orgFiles, setOrgFiles] = useState<OrgContextFile[]>([]);
  const [loadingFileIds, setLoadingFileIds] = useState<string[]>([]);
  const [error, setError] = useState<string | undefined>();

  const loadOrgFiles = useCallback(async () => {
    if (isPreviewMode) {
      setOrgFiles(MOCK_ORG_FILES);
      return;
    }
    try {
      const entries = await listContextFiles(apiOptions, project.id);
      setOrgFiles(
        entries
          .filter(e => e.source === 'org')
          .map(e => ({
            id: e.fileId,
            name: e.displayName,
            fileTypeTag: e.fileTypeTag,
            status: e.status,
            isIncluded: e.isIncluded,
          })),
      );
    } catch {
      setError(
        t('contextFiles.loadError', {
          defaultValue: 'Failed to load org files.',
        }),
      );
    }
  }, [apiOptions, isPreviewMode, project.id, t]);

  useEffect(() => {
    void loadOrgFiles();
  }, [loadOrgFiles]);

  const handleToggle = async (fileId: string, included: boolean) => {
    setLoadingFileIds(prev => [...prev, fileId]);
    try {
      if (isPreviewMode) {
        setOrgFiles(prev =>
          prev.map(f => (f.id === fileId ? { ...f, isIncluded: included } : f)),
        );
      } else {
        if (included) {
          await includeOrgFile(apiOptions, project.id, fileId);
        } else {
          await excludeOrgFile(apiOptions, project.id, fileId);
        }
        setOrgFiles(prev =>
          prev.map(f => (f.id === fileId ? { ...f, isIncluded: included } : f)),
        );
      }
    } catch {
      setError(
        t('contextFiles.toggleError', {
          defaultValue: 'Failed to update file inclusion.',
        }),
      );
    } finally {
      setLoadingFileIds(prev => prev.filter(id => id !== fileId));
    }
  };

  return (
    <InfoCard title={t('contextFiles.orgFilesTitle')}>
      {readOnly && (
        <Typography variant="body2" className={classes.readOnlyNotice}>
          {t('contextFiles.readOnlyNotice')}
        </Typography>
      )}
      {error && (
        <Box mb={1}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      )}
      <OrgContextSetSelector
        files={orgFiles}
        readOnly={readOnly}
        onToggle={handleToggle}
        loadingFileIds={loadingFileIds}
      />
    </InfoCard>
  );
}
